// ──────────────────────────────────────────────────────────────────────────────
// Questionnaire Grade Descriptions
//
// For each questionnaire × question × grade category, provides the human-readable
// interpretation text shown in the Collaboration Alignment Report and on the UI.
//
// Structure:
//   QUESTIONNAIRE_GRADE_DESCRIPTIONS[questionnaireId][questionId][gradeCategory]
//
// Grade categories (from GradeCategory type):
//   alignment | discussion | key_decision | clarification
// ──────────────────────────────────────────────────────────────────────────────

import { QUESTIONNAIRE_IDS } from "@/utils/questionnaireMapping";

export type GradeDescriptions = {
  alignment: string;
  discussion: string;
  key_decision: string;
  clarification: string;
};

export type QuestionnaireDescriptionMap = Record<string, GradeDescriptions>;

export const QUESTIONNAIRE_GRADE_DESCRIPTIONS: Partial<Record<string, QuestionnaireDescriptionMap>> = {
  // ─── Outsourcing / Managed Services & API Integration ────────────────────
  [QUESTIONNAIRE_IDS.OUTSOURCING_API]: {
    q1: {
      alignment:
        "Both parties appear to share a compatible understanding of the nature of the collaboration being explored.",
      discussion:
        "The responses suggest differing expectations regarding the type of collaboration that may benefit from further discussion.",
      key_decision: "The responses indicate materially different expectations regarding the collaboration structure.",
      clarification: "Additional information may be required to clarify the intended nature of the collaboration.",
    },
    q2: {
      alignment:
        "Both parties appear to share compatible expectations regarding the primary objective of the collaboration.",
      discussion: "The responses suggest differing priorities that may benefit from further discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding the strategic objective of the collaboration.",
      clarification: "Further information may be required to clarify the primary objective.",
    },
    q3: {
      alignment:
        "Both parties appear to share compatible expectations regarding the functional scope of the engagement.",
      discussion: "The responses suggest differing expectations regarding the functional areas involved.",
      key_decision:
        "The responses indicate materially different expectations regarding the scope of the collaboration.",
      clarification: "Additional information may be required to clarify the intended functional scope.",
    },
    q4: {
      alignment: "Both parties appear to share compatible expectations regarding the duration of the engagement.",
      discussion: "The responses suggest differing expectations regarding the engagement timeline.",
      key_decision:
        "The responses indicate materially different expectations regarding the duration of the collaboration.",
      clarification: "Further information may be required to clarify the expected duration.",
    },
    q5: {
      alignment: "Both parties appear to share compatible expectations regarding the delivery model.",
      discussion:
        "The responses suggest differing expectations regarding how delivery responsibilities will be structured.",
      key_decision: "The responses indicate materially different expectations regarding the delivery model.",
      clarification: "Additional information may be required to clarify delivery structure expectations.",
    },
    q6: {
      alignment: "Both parties appear to share compatible expectations regarding the level of system integration.",
      discussion:
        "The responses suggest differing expectations regarding integration depth that may benefit from discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding system integration requirements.",
      clarification: "Further information may be required to clarify integration expectations.",
    },
    q7: {
      alignment: "Both parties appear to share compatible expectations regarding the technology environment involved.",
      discussion:
        "The responses suggest differing expectations regarding the technology stack or platform environment.",
      key_decision: "The responses indicate materially different expectations regarding technology infrastructure.",
      clarification: "Additional information may be required to clarify the technology environment.",
    },
    q8: {
      alignment: "Both parties appear to share compatible expectations regarding system or API access levels.",
      discussion: "The responses suggest differing expectations regarding access permissions or integration levels.",
      key_decision:
        "The responses indicate materially different expectations regarding system access or integration rights.",
      clarification: "Further information may be required to clarify the required access level.",
    },
    q9: {
      alignment:
        "Both parties appear to share compatible expectations regarding system customisation and change management.",
      discussion: "The responses suggest differing expectations regarding the degree of customisation required.",
      key_decision:
        "The responses indicate materially different expectations regarding system modification or development.",
      clarification: "Additional information may be required to clarify customisation expectations.",
    },
    q10: {
      alignment: "Both parties appear to share compatible expectations regarding the commercial engagement model.",
      discussion: "The responses suggest differing commercial models that may benefit from further discussion.",
      key_decision: "The responses indicate materially different expectations regarding the commercial structure.",
      clarification: "Further information may be required to clarify the preferred commercial model.",
    },
    q11: {
      alignment: "Both parties appear to share compatible expectations regarding how pricing will be determined.",
      discussion: "The responses suggest differing approaches to pricing that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding pricing determination.",
      clarification: "Additional information may be required to clarify pricing structures.",
    },
    q12: {
      alignment: "Both parties appear to share compatible expectations regarding workload predictability.",
      discussion: "The responses suggest differing expectations regarding the predictability of work volumes.",
      key_decision: "The responses indicate materially different expectations regarding workload stability.",
      clarification: "Further information may be required to clarify workload expectations.",
    },
    q13: {
      alignment: "Both parties appear to share compatible expectations regarding performance measurement.",
      discussion: "The responses suggest differing expectations regarding service level evaluation.",
      key_decision:
        "The responses indicate materially different expectations regarding performance measurement methods.",
      clarification: "Additional information may be required to clarify performance measurement expectations.",
    },
    q14: {
      alignment: "Both parties appear to share compatible expectations regarding the criticality of the collaboration.",
      discussion: "The responses suggest differing expectations regarding the importance of the service relationship.",
      key_decision: "The responses indicate materially different expectations regarding operational dependency.",
      clarification: "Further information may be required to clarify the operational importance of the collaboration.",
    },
    q15: {
      alignment: "Both parties appear to share compatible expectations regarding data sensitivity and handling.",
      discussion: "The responses suggest differing expectations regarding data confidentiality.",
      key_decision: "The responses indicate materially different expectations regarding data protection requirements.",
      clarification: "Additional information may be required to clarify data handling expectations.",
    },
    q16: {
      alignment: "Both parties appear to share compatible expectations regarding security and compliance requirements.",
      discussion: "The responses suggest differing expectations regarding compliance or security standards.",
      key_decision:
        "The responses indicate materially different expectations regarding regulatory or compliance obligations.",
      clarification: "Further information may be required to clarify security expectations.",
    },
    q17: {
      alignment: "Both parties appear to share compatible expectations regarding governance and oversight.",
      discussion: "The responses suggest differing expectations regarding governance mechanisms.",
      key_decision: "The responses indicate materially different expectations regarding governance structures.",
      clarification: "Additional information may be required to clarify governance expectations.",
    },
    q18: {
      alignment:
        "Both parties appear to share compatible expectations regarding how performance outcomes will be measured.",
      discussion: "The responses suggest differing approaches to defining performance outcomes.",
      key_decision: "The responses indicate materially different expectations regarding performance evaluation.",
      clarification: "Further information may be required to clarify performance definitions.",
    },
    q19: {
      alignment: "Both parties appear to share compatible expectations regarding continuity of the engagement.",
      discussion: "The responses suggest differing expectations regarding transition or exit planning.",
      key_decision: "The responses indicate materially different expectations regarding long-term continuity.",
      clarification: "Additional information may be required to clarify transition expectations.",
    },
    q20: {
      alignment: "Both parties appear to share compatible expectations regarding dispute resolution mechanisms.",
      discussion: "The responses suggest differing preferences regarding dispute resolution.",
      key_decision:
        "The responses indicate materially different expectations regarding dispute resolution arrangements.",
      clarification: "Further information may be required to clarify dispute resolution preferences.",
    },
  },

  // ─── Manufacturer Collaboration ──────────────────────────────────────────
  [QUESTIONNAIRE_IDS.MANUFACTURER]: {
    q1: {
      alignment: "Both parties have clearly indicated complementary roles within the proposed licensing engagement.",
      discussion:
        "The roles indicated by the parties may require further clarification to ensure mutual expectations are aligned.",
      key_decision:
        "The responses suggest a potential mismatch in licensing roles that may require explicit agreement.",
      clarification:
        "Additional information may be required to determine the intended roles of the parties in the licensing arrangement.",
    },
    q2: {
      alignment:
        "Both parties appear to have indicated a compatible understanding of the technology involved in the licensing arrangement.",
      discussion: "The responses suggest differences in the technology scope that may benefit from further discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding the type of technology involved.",
      clarification:
        "Additional information may be required to clarify the technology category involved in the licensing engagement.",
    },
    q3: {
      alignment: "Both parties appear to have a compatible understanding of how the technology will be used.",
      discussion:
        "The responses suggest variations in the intended use of the technology that may benefit from further discussion.",
      key_decision:
        "The responses indicate significantly different expectations regarding the permitted use of the technology.",
      clarification: "Further information may be required to determine how the technology is expected to be used.",
    },
    q4: {
      alignment:
        "Both parties appear to share a compatible view regarding the maturity stage of the technology in the target market.",
      discussion:
        "The responses suggest differing perspectives on the maturity of the technology that may benefit from discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding the maturity stage of the technology.",
      clarification: "Additional information may be required to clarify the maturity level of the technology.",
    },
    q5: {
      alignment: "Both parties appear to share a compatible perspective regarding the geographic scope of the license.",
      discussion: "The responses indicate differing geographic expectations that may benefit from further discussion.",
      key_decision: "The responses suggest materially different geographic scopes that may require explicit agreement.",
      clarification: "Further information may be required to clarify the geographic scope of the license.",
    },
    q6: {
      alignment: "Both parties appear to share a compatible preference regarding the licensing model.",
      discussion: "The responses suggest differing licensing structures that may benefit from further discussion.",
      key_decision: "The responses indicate materially different expectations regarding the licensing model.",
      clarification: "Additional information may be required to clarify the preferred license structure.",
    },
    q7: {
      alignment: "Both parties appear to share a compatible approach regarding the fee or value-sharing structure.",
      discussion: "The responses indicate different financial structures that may benefit from further discussion.",
      key_decision:
        "The responses suggest materially different expectations regarding the financial structure of the licensing agreement.",
      clarification: "Additional information may be required to clarify the preferred fee or value-sharing basis.",
    },
    q8: {
      alignment: "Both parties appear to share a compatible expectation regarding license exclusivity.",
      discussion:
        "The responses suggest differing expectations regarding exclusivity that may benefit from further discussion.",
      key_decision: "The responses indicate substantially different expectations regarding exclusivity terms.",
      clarification: "Further information may be required to clarify exclusivity expectations.",
    },
    q9: {
      alignment:
        "Both parties appear to share a compatible expectation regarding the duration of the licensing arrangement.",
      discussion:
        "The responses suggest differing expectations regarding license duration that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding the license term.",
      clarification: "Additional information may be required to clarify the intended license term.",
    },
    q10: {
      alignment: "Both parties appear to share a compatible approach regarding commercial flexibility.",
      discussion:
        "The responses suggest differing expectations regarding commercial flexibility that may benefit from discussion.",
      key_decision: "The responses indicate materially different approaches to commercial terms.",
      clarification: "Further information may be required to clarify commercial flexibility expectations.",
    },
    q11: {
      alignment: "Both parties appear to share a compatible expectation regarding the level of technology access.",
      discussion: "The responses suggest differing expectations regarding access to the technology.",
      key_decision: "The responses indicate substantially different expectations regarding technology access rights.",
      clarification: "Additional information may be required to clarify the expected level of technology access.",
    },
    q12: {
      alignment:
        "Both parties appear to share a compatible perspective regarding technology customisation and modification rights.",
      discussion: "The responses suggest differing expectations regarding the scope of modification rights.",
      key_decision:
        "The responses indicate materially different expectations regarding modification or development rights.",
      clarification: "Further information may be required to clarify customisation rights.",
    },
    q13: {
      alignment: "Both parties appear to share a compatible expectation regarding support and knowledge transfer.",
      discussion: "The responses suggest differing expectations regarding the level of support required.",
      key_decision: "The responses indicate materially different expectations regarding support responsibilities.",
      clarification: "Additional information may be required to clarify support expectations.",
    },
    q14: {
      alignment: "Both parties appear to share a compatible expectation regarding technology updates and upgrades.",
      discussion: "The responses suggest differing expectations regarding updates that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding upgrade policies.",
      clarification: "Further information may be required to clarify upgrade policies.",
    },
    q15: {
      alignment: "Both parties appear to share a compatible expectation regarding reporting and oversight mechanisms.",
      discussion: "The responses suggest differing expectations regarding reporting requirements.",
      key_decision:
        "The responses indicate materially different expectations regarding oversight and reporting obligations.",
      clarification: "Additional information may be required to clarify reporting expectations.",
    },
    q16: {
      alignment:
        "Both parties appear to share a compatible expectation regarding ownership of improvements or enhancements.",
      discussion: "The responses suggest differing expectations regarding ownership of future improvements.",
      key_decision:
        "The responses indicate materially different expectations regarding intellectual property ownership.",
      clarification: "Additional information may be required to clarify improvement ownership rights.",
    },
    q17: {
      alignment: "Both parties appear to share a compatible approach to confidentiality and IP protection.",
      discussion: "The responses suggest differing expectations regarding confidentiality protections.",
      key_decision: "The responses indicate materially different expectations regarding IP protection obligations.",
      clarification: "Further information may be required to clarify confidentiality expectations.",
    },
    q18: {
      alignment:
        "Both parties appear to share a compatible expectation regarding regulatory and compliance responsibilities.",
      discussion: "The responses suggest differing expectations regarding compliance responsibilities.",
      key_decision: "The responses indicate materially different expectations regarding regulatory responsibility.",
      clarification: "Additional information may be required to clarify compliance responsibilities.",
    },
    q19: {
      alignment: "Both parties appear to share a compatible preference regarding dispute resolution mechanisms.",
      discussion:
        "The responses suggest differing preferences regarding dispute resolution that may benefit from discussion.",
      key_decision: "The responses indicate materially different dispute resolution preferences.",
      clarification: "Further information may be required to clarify dispute resolution expectations.",
    },
    q20: {
      alignment: "Both parties appear to share a compatible perspective regarding the long-term engagement model.",
      discussion:
        "The responses suggest differing expectations regarding the continuity of the licensing relationship.",
      key_decision: "The responses indicate materially different expectations regarding the continuation model.",
      clarification: "Additional information may be required to clarify engagement continuity expectations.",
    },
  },

  // ─── Distribution / Market Access ────────────────────────────────────────
  [QUESTIONNAIRE_IDS.DISTRIBUTOR]: {
    q1: {
      alignment:
        "Both parties appear to share compatible expectations regarding the operational role of the distributor in the collaboration.",
      discussion: "The responses suggest differing expectations regarding the scope of distribution responsibilities.",
      key_decision:
        "The responses indicate materially different expectations regarding the distributor's operational role.",
      clarification: "Additional information may be required to clarify the intended distribution role.",
    },
    q2: {
      alignment: "Both parties appear to share compatible expectations regarding geographic market coverage.",
      discussion: "The responses suggest differing expectations regarding territorial coverage.",
      key_decision:
        "The responses indicate materially different expectations regarding the geographic scope of distribution.",
      clarification: "Further information may be required to clarify the intended geographic coverage.",
    },
    q3: {
      alignment:
        "Both parties appear to share compatible expectations regarding the sales model used in the collaboration.",
      discussion: "The responses suggest differing expectations regarding sales channels or distribution structure.",
      key_decision: "The responses indicate materially different expectations regarding the sales model.",
      clarification: "Additional information may be required to clarify the sales model.",
    },
    q4: {
      alignment:
        "Both parties appear to share compatible expectations regarding sector expertise and product knowledge.",
      discussion: "The responses suggest differing expectations regarding the level of domain expertise required.",
      key_decision:
        "The responses indicate materially different expectations regarding the distributor's technical or sector expertise.",
      clarification: "Further information may be required to clarify domain expertise expectations.",
    },
    q5: {
      alignment:
        "Both parties appear to share compatible expectations regarding operational systems and digital readiness.",
      discussion:
        "The responses suggest differing expectations regarding systems integration or operational readiness.",
      key_decision:
        "The responses indicate materially different expectations regarding technology or operational infrastructure.",
      clarification: "Additional information may be required to clarify operational readiness.",
    },
    q6: {
      alignment: "Both parties appear to share compatible expectations regarding the type of commercial collaboration.",
      discussion: "The responses suggest differing collaboration preferences that may benefit from discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding the structure of the collaboration.",
      clarification: "Further information may be required to clarify the preferred collaboration structure.",
    },
    q7: {
      alignment: "Both parties appear to share compatible expectations regarding exclusivity arrangements.",
      discussion: "The responses suggest differing expectations regarding exclusivity terms.",
      key_decision: "The responses indicate materially different expectations regarding exclusivity commitments.",
      clarification: "Additional information may be required to clarify exclusivity expectations.",
    },
    q8: {
      alignment: "Both parties appear to share compatible expectations regarding minimum commercial commitments.",
      discussion: "The responses suggest differing expectations regarding revenue or volume commitments.",
      key_decision:
        "The responses indicate materially different expectations regarding commercial commitment requirements.",
      clarification: "Further information may be required to clarify commitment expectations.",
    },
    q9: {
      alignment:
        "Both parties appear to share compatible expectations regarding pricing authority and commercial control.",
      discussion: "The responses suggest differing expectations regarding pricing control.",
      key_decision: "The responses indicate materially different expectations regarding commercial pricing authority.",
      clarification: "Additional information may be required to clarify pricing responsibilities.",
    },
    q10: {
      alignment:
        "Both parties appear to share compatible expectations regarding brand representation responsibilities.",
      discussion: "The responses suggest differing expectations regarding marketing or brand responsibilities.",
      key_decision:
        "The responses indicate materially different expectations regarding brand ownership and representation.",
      clarification: "Further information may be required to clarify brand representation expectations.",
    },
    q11: {
      alignment:
        "Both parties appear to share compatible expectations regarding inventory management and working capital support.",
      discussion: "The responses suggest differing expectations regarding inventory or financing responsibilities.",
      key_decision: "The responses indicate materially different expectations regarding working capital support.",
      clarification: "Additional information may be required to clarify inventory responsibilities.",
    },
    q12: {
      alignment: "Both parties appear to share compatible expectations regarding payment timelines.",
      discussion: "The responses suggest differing payment expectations that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding payment terms.",
      clarification: "Further information may be required to clarify payment arrangements.",
    },
    q13: {
      alignment: "Both parties appear to share compatible expectations regarding investment commitments.",
      discussion: "The responses suggest differing expectations regarding financial or operational investments.",
      key_decision: "The responses indicate materially different expectations regarding investment commitments.",
      clarification: "Additional information may be required to clarify investment willingness.",
    },
    q14: {
      alignment: "Both parties appear to share compatible expectations regarding the revenue model.",
      discussion: "The responses suggest differing expectations regarding how revenue will be structured.",
      key_decision:
        "The responses indicate materially different expectations regarding the financial structure of the collaboration.",
      clarification: "Further information may be required to clarify revenue-sharing arrangements.",
    },
    q15: {
      alignment: "Both parties appear to share compatible expectations regarding financial risk tolerance.",
      discussion: "The responses suggest differing risk perspectives that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding acceptable financial risk.",
      clarification: "Additional information may be required to clarify risk appetite.",
    },
    q16: {
      alignment: "Both parties appear to share compatible expectations regarding intellectual property handling.",
      discussion: "The responses suggest differing expectations regarding IP management.",
      key_decision: "The responses indicate materially different expectations regarding intellectual property rights.",
      clarification: "Further information may be required to clarify IP handling expectations.",
    },
    q17: {
      alignment: "Both parties appear to share compatible expectations regarding data and system access.",
      discussion: "The responses suggest differing expectations regarding data sharing or system integration.",
      key_decision: "The responses indicate materially different expectations regarding system or data access.",
      clarification: "Additional information may be required to clarify data access expectations.",
    },
    q18: {
      alignment: "Both parties appear to share compatible expectations regarding governance and oversight.",
      discussion: "The responses suggest differing expectations regarding governance mechanisms.",
      key_decision: "The responses indicate materially different expectations regarding governance structures.",
      clarification: "Further information may be required to clarify governance expectations.",
    },
    q19: {
      alignment: "Both parties appear to share compatible expectations regarding dispute resolution mechanisms.",
      discussion: "The responses suggest differing preferences regarding dispute resolution.",
      key_decision: "The responses indicate materially different dispute resolution expectations.",
      clarification: "Additional information may be required to clarify dispute resolution preferences.",
    },
    q20: {
      alignment: "Both parties appear to share compatible expectations regarding the long-term engagement structure.",
      discussion: "The responses suggest differing expectations regarding continuation of the collaboration.",
      key_decision:
        "The responses indicate materially different expectations regarding the long-term partnership model.",
      clarification: "Further information may be required to clarify engagement continuity expectations.",
    },
  },

  // ─── Technology Licensing ────────────────────────────────────────────────
  [QUESTIONNAIRE_IDS.TECHNOLOGY_LICENSING]: {
    q1: {
      alignment: "Both parties have clearly indicated complementary roles within the proposed licensing engagement.",
      discussion:
        "The roles indicated by the parties may require further clarification to ensure mutual expectations are aligned.",
      key_decision:
        "The responses suggest a potential mismatch in licensing roles that may require explicit agreement.",
      clarification:
        "Additional information may be required to determine the intended roles of the parties in the licensing arrangement.",
    },
    q2: {
      alignment:
        "Both parties appear to have indicated a compatible understanding of the technology involved in the licensing arrangement.",
      discussion: "The responses suggest differences in the technology scope that may benefit from further discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding the type of technology involved.",
      clarification:
        "Additional information may be required to clarify the technology category involved in the licensing engagement.",
    },
    q3: {
      alignment: "Both parties appear to have a compatible understanding of how the technology will be used.",
      discussion:
        "The responses suggest variations in the intended use of the technology that may benefit from further discussion.",
      key_decision:
        "The responses indicate significantly different expectations regarding the permitted use of the technology.",
      clarification: "Further information may be required to determine how the technology is expected to be used.",
    },
    q4: {
      alignment:
        "Both parties appear to share a compatible view regarding the maturity stage of the technology in the target market.",
      discussion:
        "The responses suggest differing perspectives on the maturity of the technology that may benefit from discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding the maturity stage of the technology.",
      clarification: "Additional information may be required to clarify the maturity level of the technology.",
    },
    q5: {
      alignment: "Both parties appear to share a compatible perspective regarding the geographic scope of the license.",
      discussion: "The responses indicate differing geographic expectations that may benefit from further discussion.",
      key_decision: "The responses suggest materially different geographic scopes that may require explicit agreement.",
      clarification: "Further information may be required to clarify the geographic scope of the license.",
    },
    q6: {
      alignment: "Both parties appear to share a compatible preference regarding the licensing model.",
      discussion: "The responses suggest differing licensing structures that may benefit from further discussion.",
      key_decision: "The responses indicate materially different expectations regarding the licensing model.",
      clarification: "Additional information may be required to clarify the preferred license structure.",
    },
    q7: {
      alignment: "Both parties appear to share a compatible approach regarding the fee or value-sharing structure.",
      discussion: "The responses indicate different financial structures that may benefit from further discussion.",
      key_decision:
        "The responses suggest materially different expectations regarding the financial structure of the licensing agreement.",
      clarification: "Additional information may be required to clarify the preferred fee or value-sharing basis.",
    },
    q8: {
      alignment: "Both parties appear to share a compatible expectation regarding license exclusivity.",
      discussion:
        "The responses suggest differing expectations regarding exclusivity that may benefit from further discussion.",
      key_decision: "The responses indicate substantially different expectations regarding exclusivity terms.",
      clarification: "Further information may be required to clarify exclusivity expectations.",
    },
    q9: {
      alignment:
        "Both parties appear to share a compatible expectation regarding the duration of the licensing arrangement.",
      discussion:
        "The responses suggest differing expectations regarding license duration that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding the license term.",
      clarification: "Additional information may be required to clarify the intended license term.",
    },
    q10: {
      alignment: "Both parties appear to share a compatible approach regarding commercial flexibility.",
      discussion:
        "The responses suggest differing expectations regarding commercial flexibility that may benefit from discussion.",
      key_decision: "The responses indicate materially different approaches to commercial terms.",
      clarification: "Further information may be required to clarify commercial flexibility expectations.",
    },
    q11: {
      alignment: "Both parties appear to share a compatible expectation regarding the level of technology access.",
      discussion: "The responses suggest differing expectations regarding access to the technology.",
      key_decision: "The responses indicate substantially different expectations regarding technology access rights.",
      clarification: "Additional information may be required to clarify the expected level of technology access.",
    },
    q12: {
      alignment:
        "Both parties appear to share a compatible perspective regarding technology customisation and modification rights.",
      discussion: "The responses suggest differing expectations regarding the scope of modification rights.",
      key_decision:
        "The responses indicate materially different expectations regarding modification or development rights.",
      clarification: "Further information may be required to clarify customisation rights.",
    },
    q13: {
      alignment: "Both parties appear to share a compatible expectation regarding support and knowledge transfer.",
      discussion: "The responses suggest differing expectations regarding the level of support required.",
      key_decision: "The responses indicate materially different expectations regarding support responsibilities.",
      clarification: "Additional information may be required to clarify support expectations.",
    },
    q14: {
      alignment: "Both parties appear to share a compatible expectation regarding technology updates and upgrades.",
      discussion: "The responses suggest differing expectations regarding updates that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding upgrade policies.",
      clarification: "Further information may be required to clarify upgrade policies.",
    },
    q15: {
      alignment: "Both parties appear to share a compatible expectation regarding reporting and oversight mechanisms.",
      discussion: "The responses suggest differing expectations regarding reporting requirements.",
      key_decision:
        "The responses indicate materially different expectations regarding oversight and reporting obligations.",
      clarification: "Additional information may be required to clarify reporting expectations.",
    },
    q16: {
      alignment:
        "Both parties appear to share a compatible expectation regarding ownership of improvements or enhancements.",
      discussion: "The responses suggest differing expectations regarding ownership of future improvements.",
      key_decision:
        "The responses indicate materially different expectations regarding intellectual property ownership.",
      clarification: "Additional information may be required to clarify improvement ownership rights.",
    },
    q17: {
      alignment: "Both parties appear to share a compatible approach to confidentiality and IP protection.",
      discussion: "The responses suggest differing expectations regarding confidentiality protections.",
      key_decision: "The responses indicate materially different expectations regarding IP protection obligations.",
      clarification: "Further information may be required to clarify confidentiality expectations.",
    },
    q18: {
      alignment:
        "Both parties appear to share a compatible expectation regarding regulatory and compliance responsibilities.",
      discussion: "The responses suggest differing expectations regarding compliance responsibilities.",
      key_decision: "The responses indicate materially different expectations regarding regulatory responsibility.",
      clarification: "Additional information may be required to clarify compliance responsibilities.",
    },
    q19: {
      alignment: "Both parties appear to share a compatible preference regarding dispute resolution mechanisms.",
      discussion:
        "The responses suggest differing preferences regarding dispute resolution that may benefit from discussion.",
      key_decision: "The responses indicate materially different dispute resolution preferences.",
      clarification: "Further information may be required to clarify dispute resolution expectations.",
    },
    q20: {
      alignment: "Both parties appear to share a compatible perspective regarding the long-term engagement model.",
      discussion:
        "The responses suggest differing expectations regarding the continuity of the licensing relationship.",
      key_decision: "The responses indicate materially different expectations regarding the continuation model.",
      clarification: "Additional information may be required to clarify engagement continuity expectations.",
    },
  },

  // ─── R&D / Co-Development ────────────────────────────────────────────────
  [QUESTIONNAIRE_IDS.RND]: {
    q1: {
      alignment: "Both parties appear to share a compatible objective for the R&D collaboration.",
      discussion: "The responses indicate differing R&D objectives that may benefit from further discussion.",
      key_decision:
        "The responses suggest materially different expectations regarding the primary objective of the collaboration.",
      clarification:
        "Additional information may be required to clarify the intended objective of the R&D collaboration.",
    },
    q2: {
      alignment: "Both parties appear to share compatible expectations regarding the stage of R&D engagement.",
      discussion: "The responses suggest differing expectations regarding the stage of R&D involvement.",
      key_decision: "The responses indicate materially different expectations regarding the stage of the R&D process.",
      clarification: "Further information may be required to clarify the stage of R&D engagement.",
    },
    q3: {
      alignment:
        "Both parties appear to share compatible expectations regarding the expected outcomes of the R&D collaboration.",
      discussion: "The responses suggest differing expectations regarding R&D deliverables.",
      key_decision:
        "The responses indicate materially different expectations regarding the final outcomes of the R&D effort.",
      clarification: "Additional information may be required to clarify the expected R&D outputs.",
    },
    q4: {
      alignment: "Both parties appear to share a compatible focus regarding the industry or application area.",
      discussion: "The responses suggest differing application areas that may benefit from further discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding the target industry or application.",
      clarification: "Further information may be required to clarify the intended application focus.",
    },
    q5: {
      alignment:
        "Both parties appear to share compatible expectations regarding the geographic scope of the R&D collaboration.",
      discussion: "The responses suggest differing expectations regarding the geographic scope.",
      key_decision: "The responses indicate materially different geographic expectations for the R&D collaboration.",
      clarification: "Additional information may be required to clarify the geographic scope.",
    },
    q6: {
      alignment:
        "The contributions indicated by both parties appear complementary and supportive of the R&D collaboration.",
      discussion:
        "The responses suggest differing or partially overlapping contributions that may benefit from discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding contributions to the R&D effort.",
      clarification: "Further information may be required to clarify the contributions each party intends to bring.",
    },
    q7: {
      alignment: "Both parties appear to share compatible expectations regarding partner contributions.",
      discussion: "The responses suggest differing expectations regarding contributions from the partner.",
      key_decision: "The responses indicate materially different expectations regarding partner contributions.",
      clarification: "Additional information may be required to clarify expectations regarding partner contributions.",
    },
    q8: {
      alignment: "Both parties appear to share compatible expectations regarding the level of resource commitment.",
      discussion: "The responses suggest differing expectations regarding resource allocation.",
      key_decision: "The responses indicate materially different expectations regarding resource commitment.",
      clarification: "Further information may be required to clarify resource commitment levels.",
    },
    q9: {
      alignment:
        "Both parties appear to share compatible expectations regarding infrastructure or tools required for the R&D collaboration.",
      discussion: "The responses suggest differing infrastructure expectations that may benefit from discussion.",
      key_decision:
        "The responses indicate materially different expectations regarding infrastructure or technological capabilities.",
      clarification: "Additional information may be required to clarify infrastructure availability.",
    },
    q10: {
      alignment: "Both parties appear to share compatible expectations regarding the co-development approach.",
      discussion:
        "The responses suggest differing approaches to collaboration that may benefit from further discussion.",
      key_decision: "The responses indicate materially different expectations regarding the co-development structure.",
      clarification: "Further information may be required to clarify the development approach.",
    },
    q11: {
      alignment:
        "Both parties appear to share compatible expectations regarding the funding model for the R&D collaboration.",
      discussion: "The responses suggest differing expectations regarding R&D funding arrangements.",
      key_decision:
        "The responses indicate materially different expectations regarding financial responsibility for the R&D project.",
      clarification: "Additional information may be required to clarify the R&D funding structure.",
    },
    q12: {
      alignment: "Both parties appear to share compatible expectations regarding cost recovery.",
      discussion: "The responses suggest differing expectations regarding cost recovery mechanisms.",
      key_decision:
        "The responses indicate materially different expectations regarding financial recovery of R&D investment.",
      clarification: "Further information may be required to clarify cost recovery expectations.",
    },
    q13: {
      alignment:
        "Both parties appear to share compatible expectations regarding the commercialisation pathway after R&D.",
      discussion: "The responses suggest differing commercialisation approaches that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding post-R&D commercialisation.",
      clarification: "Additional information may be required to clarify commercialisation intentions.",
    },
    q14: {
      alignment:
        "Both parties appear to share compatible expectations regarding acceptable levels of R&D risk and uncertainty.",
      discussion: "The responses suggest differing risk perspectives that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding acceptable risk levels.",
      clarification: "Further information may be required to clarify risk tolerance.",
    },
    q15: {
      alignment:
        "Both parties appear to share compatible expectations regarding the timeline of the R&D collaboration.",
      discussion: "The responses suggest differing expectations regarding project timelines.",
      key_decision: "The responses indicate materially different expectations regarding R&D duration.",
      clarification: "Additional information may be required to clarify timeline expectations.",
    },
    q16: {
      alignment:
        "Both parties appear to share compatible expectations regarding how existing intellectual property will be handled.",
      discussion: "The responses suggest differing expectations regarding the treatment of background IP.",
      key_decision: "The responses indicate materially different expectations regarding IP ownership or licensing.",
      clarification: "Further information may be required to clarify how background IP will be handled.",
    },
    q17: {
      alignment:
        "Both parties appear to share compatible expectations regarding ownership of newly created intellectual property.",
      discussion: "The responses suggest differing expectations regarding ownership of new IP.",
      key_decision:
        "The responses indicate materially different expectations regarding ownership of innovation outcomes.",
      clarification: "Additional information may be required to clarify IP ownership arrangements.",
    },
    q18: {
      alignment: "Both parties appear to share compatible expectations regarding confidentiality and data sharing.",
      discussion: "The responses suggest differing expectations regarding data sharing or confidentiality.",
      key_decision:
        "The responses indicate materially different expectations regarding protection of confidential information.",
      clarification: "Further information may be required to clarify confidentiality expectations.",
    },
    q19: {
      alignment:
        "Both parties appear to share compatible expectations regarding governance and decision-making structures.",
      discussion: "The responses suggest differing governance preferences that may benefit from discussion.",
      key_decision: "The responses indicate materially different expectations regarding governance and oversight.",
      clarification: "Additional information may be required to clarify governance arrangements.",
    },
    q20: {
      alignment:
        "Both parties appear to share compatible expectations regarding the pathway after the R&D collaboration concludes.",
      discussion: "The responses suggest differing expectations regarding next steps following R&D completion.",
      key_decision: "The responses indicate materially different expectations regarding the post-R&D relationship.",
      clarification: "Further information may be required to clarify the preferred continuation pathway.",
    },
  },

  // ─── Joint Venture ───────────────────────────────────────────────────────
  [QUESTIONNAIRE_IDS.JV]: {
    q1: {
      alignment: "Both parties have indicated a similar strategic objective for the proposed joint venture.",
      discussion:
        "The responses indicate different strategic priorities that may benefit from further discussion between the parties.",
      key_decision:
        "The responses suggest materially different strategic objectives that may require a clear decision before proceeding.",
      clarification:
        "Additional information may be required to fully understand the intended strategic objective of the joint venture.",
    },
    q2: {
      alignment: "Both parties appear to share a similar expectation regarding the duration of the joint venture.",
      discussion: "The responses suggest variations in expected duration that may benefit from further discussion.",
      key_decision:
        "The responses indicate significantly different expectations regarding the time horizon of the joint venture.",
      clarification: "Additional information may be required to clarify the expected duration of the joint venture.",
    },
    q3: {
      alignment: "Both parties have indicated a compatible geographic focus for the joint venture.",
      discussion: "The responses suggest differing geographic priorities that may require further discussion.",
      key_decision:
        "The responses indicate substantially different geographic scopes that may require explicit alignment.",
      clarification:
        "Further information may be required to determine the intended geographic focus of the joint venture.",
    },
    q4: {
      alignment:
        "Both parties appear to share a similar expectation regarding the maturity level of the proposed joint venture.",
      discussion: "The responses indicate different expectations regarding the stage or maturity of the collaboration.",
      key_decision:
        "The responses suggest materially different expectations regarding the operational stage of the joint venture.",
      clarification:
        "Additional details may be required to better understand the maturity expectations for the joint venture.",
    },
    q5: {
      alignment: "The contributions indicated by both parties appear complementary and aligned for the joint venture.",
      discussion:
        "The responses indicate different or partially overlapping contributions that may require further discussion.",
      key_decision:
        "The contributions indicated by both parties suggest a significant structural difference that may require clarification of roles.",
      clarification:
        "Additional information may be required to clearly understand the contributions each party intends to bring to the joint venture.",
    },
    q6: {
      alignment:
        "Both parties appear to have compatible expectations regarding the contributions expected from the partner.",
      discussion: "The expectations indicated by the parties may differ and could benefit from further clarification.",
      key_decision: "The responses suggest significantly different expectations regarding partner contributions.",
      clarification:
        "Further information may be required to clarify expectations regarding contributions from the other party.",
    },
    q7: {
      alignment:
        "Both parties appear to share a compatible perspective regarding the role of intellectual property within the joint venture.",
      discussion: "The responses indicate differing expectations regarding intellectual property arrangements.",
      key_decision:
        "The responses suggest substantially different expectations regarding IP ownership or usage within the joint venture.",
      clarification:
        "Additional information may be required to clarify how intellectual property will be addressed in the collaboration.",
    },
    q8: {
      alignment:
        "Both parties have indicated a compatible approach regarding responsibility for commercialising the joint venture's outputs.",
      discussion: "The responses indicate differing expectations regarding commercialisation responsibility.",
      key_decision:
        "The responses suggest materially different expectations regarding how commercialisation will be managed.",
      clarification:
        "Further information may be required to determine how commercialisation responsibilities may be allocated.",
    },
    q9: {
      alignment:
        "Both parties appear to have a compatible approach regarding capital contributions to the joint venture.",
      discussion:
        "The responses indicate different capital contribution preferences that may benefit from further discussion.",
      key_decision:
        "The responses suggest substantially different expectations regarding how capital contributions should be structured.",
      clarification: "Additional information may be required to clarify the intended capital contribution model.",
    },
    q10: {
      alignment: "Both parties appear to share a compatible expectation regarding ownership structure.",
      discussion: "The responses indicate differing ownership expectations that may require further discussion.",
      key_decision:
        "The responses suggest significantly different ownership expectations that may require explicit agreement.",
      clarification: "Further information may be required to clarify ownership expectations.",
    },
    q11: {
      alignment: "Both parties appear to have a compatible perspective regarding profit and loss sharing arrangements.",
      discussion:
        "The responses indicate different approaches to profit and loss sharing that may benefit from discussion.",
      key_decision:
        "The responses suggest materially different expectations regarding how financial outcomes will be shared.",
      clarification: "Additional information may be required to clarify profit and loss sharing expectations.",
    },
    q12: {
      alignment: "Both parties appear to share a compatible risk appetite for the proposed collaboration.",
      discussion: "The responses suggest differing risk perspectives that may benefit from further discussion.",
      key_decision: "The responses indicate substantially different risk appetites which may require clear alignment.",
      clarification:
        "Further information may be required to understand the risk appetite of the participating companies.",
    },
    q13: {
      alignment:
        "Both parties appear to share a compatible view regarding the governance structure of the joint venture.",
      discussion: "The responses indicate differing expectations regarding governance arrangements.",
      key_decision: "The responses suggest materially different expectations regarding governance and oversight.",
      clarification: "Additional information may be required to clarify governance structure preferences.",
    },
    q14: {
      alignment:
        "Both parties appear to share a compatible perspective regarding decision-making authority within the joint venture.",
      discussion: "The responses indicate differing expectations regarding decision-making processes.",
      key_decision:
        "The responses suggest significantly different views on decision authority which may require explicit agreement.",
      clarification: "Further information may be required to clarify decision-making authority.",
    },
    q15: {
      alignment: "Both parties appear to have a compatible expectation regarding operational responsibility.",
      discussion: "The responses suggest differing views regarding operational responsibility.",
      key_decision: "The responses indicate significantly different expectations regarding operational control.",
      clarification: "Additional information may be required to clarify operational responsibility.",
    },
    q16: {
      alignment: "Both parties appear to share a compatible expectation regarding reporting and oversight.",
      discussion: "The responses indicate differing expectations regarding oversight mechanisms.",
      key_decision:
        "The responses suggest materially different expectations regarding governance transparency and oversight.",
      clarification: "Further information may be required to determine reporting expectations.",
    },
    q17: {
      alignment:
        "Both parties appear to share a compatible perspective regarding the long-term exit philosophy of the joint venture.",
      discussion: "The responses indicate differing expectations regarding exit approach.",
      key_decision:
        "The responses suggest substantially different exit expectations that may require explicit alignment.",
      clarification: "Additional information may be required to clarify exit philosophy.",
    },
    q18: {
      alignment: "Both parties appear to share a compatible view regarding potential exit triggers.",
      discussion: "The responses indicate differing expectations regarding exit triggers.",
      key_decision: "The responses suggest materially different expectations regarding exit conditions.",
      clarification: "Further information may be required to clarify exit trigger expectations.",
    },
    q19: {
      alignment: "Both parties appear to share a compatible preference regarding dispute resolution mechanisms.",
      discussion: "The responses indicate differing preferences for dispute resolution.",
      key_decision:
        "The responses suggest materially different dispute resolution approaches which may require explicit agreement.",
      clarification: "Additional information may be required to clarify dispute resolution preferences.",
    },
    q20: {
      alignment: "Both parties appear to share a compatible mindset regarding the continuity of the collaboration.",
      discussion: "The responses indicate differing expectations regarding the long-term engagement approach.",
      key_decision: "The responses suggest materially different perspectives regarding collaboration continuity.",
      clarification: "Additional information may be required to clarify the engagement mindset.",
    },
  },
};

/**
 * Returns the grade description for a specific question in a questionnaire.
 * Returns undefined if no description has been defined yet.
 */
export function getGradeDescription(
  questionnaireId: string,
  questionId: string,
  category: "alignment" | "discussion" | "key_decision" | "clarification"
): string | undefined {
  return QUESTIONNAIRE_GRADE_DESCRIPTIONS[questionnaireId]?.[questionId]?.[category];
}
